import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { CalendarClock, Phone, MessageCircle, Mail, Users, MoreHorizontal } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { toDateTimeLocal, fromDateTimeLocal } from "../../utils/datetime";
import { AppModal, AppButton } from "../ui";
import "./follow-up.scss";

/**
 * Schedule (or reschedule) the next action on an enquiry.
 *
 * Quick presets carry the weight here: a counsellor hanging up a call wants
 * "tomorrow" in one tap, not a date picker. The exact-time field is there for
 * the cases that genuinely need it ("she asked me to ring at 4").
 *
 * Times round-trip through utils/datetime — a raw datetime-local value has no
 * timezone, so posting it directly makes the server read IST wall-clock as UTC
 * and everything lands 5h30m late.
 */

const TYPES = [
  { value: "call", label: "Call", icon: Phone },
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { value: "email", label: "Email", icon: Mail },
  { value: "meeting", label: "Meeting", icon: Users },
  { value: "other", label: "Other", icon: MoreHorizontal },
];

/** Presets snap to a sensible working hour rather than "now + 24h exactly". */
function presetDate(kind) {
  const d = new Date();
  switch (kind) {
    case "2h":
      d.setHours(d.getHours() + 2);
      return d;
    case "tomorrow":
      d.setDate(d.getDate() + 1);
      d.setHours(10, 0, 0, 0);
      return d;
    case "3days":
      d.setDate(d.getDate() + 3);
      d.setHours(10, 0, 0, 0);
      return d;
    case "nextweek":
      d.setDate(d.getDate() + 7);
      d.setHours(10, 0, 0, 0);
      return d;
    default:
      return d;
  }
}

const PRESETS = [
  { key: "2h", label: "In 2 hours" },
  { key: "tomorrow", label: "Tomorrow 10am" },
  { key: "3days", label: "In 3 days" },
  { key: "nextweek", label: "Next week" },
];

export default function FollowUpModal({
  show,
  onHide,
  enquiry,
  followUp = null,      // pass an existing row to reschedule instead of create
  callLogId = null,     // set when scheduled straight off a call
  onSaved,
}) {
  const editing = !!followUp;

  const [dueAt, setDueAt] = useState("");
  const [type, setType] = useState("call");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    setError("");
    if (followUp) {
      setDueAt(toDateTimeLocal(followUp.dueAt));
      setType(followUp.type || "call");
      setNote(followUp.note || "");
    } else {
      setDueAt(toDateTimeLocal(presetDate("tomorrow")));
      setType("call");
      setNote("");
    }
  }, [show, followUp]);

  const applyPreset = (kind) => setDueAt(toDateTimeLocal(presetDate(kind)));

  const handleSave = async () => {
    const iso = fromDateTimeLocal(dueAt);
    if (!iso) {
      setError("Pick a date and time.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (editing) {
        await apiClient.patch(`/follow-ups/${followUp._id}`, { dueAt: iso, type, note });
      } else {
        await apiClient.post("/follow-ups", {
          enquiryId: enquiry?._id,
          dueAt: iso,
          type,
          note,
          ...(callLogId ? { callLogId } : {}),
        });
      }
      onSaved?.();
      onHide?.();
    } catch (ex) {
      setError(ex?.response?.data?.message || "Could not save the follow-up.");
    }
    setSaving(false);
  };

  return (
    <AppModal
      show={show}
      onHide={onHide}
      title={editing ? "Reschedule follow-up" : "Schedule follow-up"}
      icon={<CalendarClock size={18} />}
      size="sm"
      footer={
        <>
          <AppButton variant="ghost" size="sm" onClick={onHide} disabled={saving}>
            Cancel
          </AppButton>
          <AppButton size="sm" onClick={handleSave} disabled={saving || !dueAt}>
            {saving ? <Spinner animation="border" size="sm" /> : editing ? "Reschedule" : "Schedule"}
          </AppButton>
        </>
      }
    >
      {enquiry && (
        <p className="fu-for">
          For <strong>{enquiry.fullName || enquiry.mobile || "this enquiry"}</strong>
        </p>
      )}

      <div className="fu-field">
        <span className="fu-label">When</span>
        <div className="fu-presets">
          {PRESETS.map((p) => (
            <button key={p.key} type="button" className="fu-preset" onClick={() => applyPreset(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
        <input
          type="datetime-local"
          className="fu-input"
          value={dueAt}
          onChange={(e) => setDueAt(e.target.value)}
        />
      </div>

      <div className="fu-field">
        <span className="fu-label">Action</span>
        <div className="fu-types">
          {TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.value}
                type="button"
                className={`fu-type ${type === t.value ? "is-active" : ""}`}
                onClick={() => setType(t.value)}
              >
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="fu-field">
        <span className="fu-label">Note <span className="fu-optional">(optional)</span></span>
        <textarea
          rows={3}
          className="fu-input"
          placeholder="e.g. Asked to call after her exam results"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      {error && <div className="fu-error">{error}</div>}
    </AppModal>
  );
}
